import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
} from '@mui/material';
import {
  Receipt,
  Add,
  Edit,
  Delete,
  Send,
  Download,
  Visibility,
  AttachMoney,
  Schedule,
  Business,
  CheckCircle,
  Pending,
  Cancel,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import timezoneService from '../services/timezoneService';

interface Invoice {
  id: string;
  invoiceNumber: string;
  clientName: string;
  projectName: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  issueDate: string;
  dueDate: string;
  description: string;
}

export const Invoices: React.FC = () => {
  const brandColors = useBrandColors();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    const mockInvoices: Invoice[] = [
      {
        id: '1',
        invoiceNumber: 'INV-2025-001',
        clientName: 'Acme Corporation',
        projectName: 'Website Development',
        amount: 2500.00,
        status: 'sent',
        issueDate: '2025-07-15',
        dueDate: '2025-08-15',
        description: 'Web development services for Q3 2025'
      },
      {
        id: '2',
        invoiceNumber: 'INV-2025-002',
        clientName: 'Tech Solutions Inc',
        projectName: 'Mobile App Design',
        amount: 1800.00,
        status: 'paid',
        issueDate: '2025-07-01',
        dueDate: '2025-08-01',
        description: 'UI/UX design for mobile application'
      },
      {
        id: '3',
        invoiceNumber: 'INV-2025-003',
        clientName: 'StartupXYZ',
        projectName: 'Consulting Services',
        amount: 950.00,
        status: 'draft',
        issueDate: '2025-07-29',
        dueDate: '2025-08-29',
        description: 'Technical consulting and code review'
      }
    ];
    setInvoices(mockInvoices);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'sent': return 'info';
      case 'overdue': return 'error';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle />;
      case 'sent': return <Send />;
      case 'overdue': return <Cancel />;
      case 'draft': return <Pending />;
      default: return <Pending />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Convert UTC date string to user timezone and format
    const userDate = timezoneService.convertUTCToUserTime(dateString);
    return timezoneService.formatUserDate(userDate);
  };

  const totalAmount = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const paidAmount = invoices
    .filter(invoice => invoice.status === 'paid')
    .reduce((sum, invoice) => sum + invoice.amount, 0);
  const pendingAmount = totalAmount - paidAmount;

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    summaryCard: {
      textAlign: 'center',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px'
    },
    summaryLabel: {
      fontSize: '14px',
      color: '#666'
    },
    actionButton: {
      marginBottom: '24px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Receipt sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Invoices</h1>
      </div>
      
      <p style={styles.subtitle}>
        Manage your invoices, track payments, and generate professional billing documents for your clients.
      </p>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <Card sx={styles.summaryCard}>
          <CardContent>
            <AttachMoney sx={{ fontSize: 32, color: brandColors.secondary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.secondary }}>
              {formatCurrency(totalAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Total Invoiced</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 32, color: brandColors.primary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.primary }}>
              {formatCurrency(paidAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Paid</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <Schedule sx={{ fontSize: 32, color: brandColors.accent, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.accent }}>
              {formatCurrency(pendingAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Pending</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <Receipt sx={{ fontSize: 32, color: brandColors.secondary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.secondary }}>
              {invoices.length}
            </Typography>
            <Typography sx={styles.summaryLabel}>Total Invoices</Typography>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <Button
        variant="contained"
        startIcon={<Add />}
        sx={{
          ...styles.actionButton,
          backgroundColor: brandColors.primary,
          '&:hover': { backgroundColor: brandColors.secondary }
        }}
        onClick={() => alert('Create Invoice functionality will be implemented')}
      >
        Create Invoice
      </Button>

      {/* Invoices Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Receipt />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Invoice List
          </Typography>
          <Chip
            label={`${invoices.length} invoices`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Issue Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {invoice.invoiceNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {invoice.clientName}
                    </Box>
                  </TableCell>
                  <TableCell>{invoice.projectName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatCurrency(invoice.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(invoice.status)}
                      label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      color={getStatusColor(invoice.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                  <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Invoice">
                        <IconButton size="small" color="primary">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Invoice">
                        <IconButton size="small" color="primary">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download PDF">
                        <IconButton size="small" color="primary">
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Invoice">
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {invoices.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Receipt sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No invoices yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first invoice to start billing your clients.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <AlertTitle>Coming Soon</AlertTitle>
        Full invoice management features including PDF generation, email sending, and payment tracking will be implemented in future updates.
      </Alert>
    </div>
  );
};

export default Invoices;
